/**
 * SSE (Server-Sent Events) parser for Claude Agent SDK streaming.
 *
 * Parses text/event-stream responses from the FastAPI backend into typed events.
 */

import type { AgentStreamEvent } from "./types";

/**
 * Parse a single SSE frame into typed events.
 *
 * SSE format:
 * event: optional
 * id: optional
 * data: {...}
 * data: {...} (multi-line payload supported)
 *
 * Returns an array because a single frame may contain concatenated JSON objects
 * due to buffering issues (e.g. `{...}\n{...}` on a single data: line).
 */
function parseSSEFrame(frame: string): AgentStreamEvent[] {
	if (!frame.trim()) {
		return [];
	}

	const lines = frame.split("\n");
	const dataLines: string[] = [];

	for (const line of lines) {
		const trimmed = line.trim();
		if (!trimmed || trimmed.startsWith(":")) {
			continue;
		}

		if (line.startsWith("data:")) {
			// Preserve internal payload whitespace, only strip the single prefix + optional leading space.
			dataLines.push(line.slice(5).replace(/^\s?/, ""));
		}
	}

	if (!dataLines.length) {
		return [];
	}

	const payload = dataLines.join("\n").trim();
	if (!payload) {
		return [];
	}

	if (payload === "[DONE]") {
		return [{ type: "done" } as AgentStreamEvent];
	}

	try {
		return [JSON.parse(payload) as AgentStreamEvent];
	} catch {
		// Fallback: split concatenated JSON objects ({...}\n{...} or {...}{...})
		const results: AgentStreamEvent[] = [];
		const parts = payload.split(/\}\s*\{/);
		if (parts.length > 1) {
			for (let i = 0; i < parts.length; i++) {
				const chunk = (i > 0 ? "{" : "") + parts[i] + (i < parts.length - 1 ? "}" : "");
				try {
					results.push(JSON.parse(chunk) as AgentStreamEvent);
				} catch {
					console.warn("Failed to parse SSE JSON fragment:", chunk);
				}
			}
		}
		if (results.length === 0) {
			console.warn("Failed to parse SSE JSON:", payload);
		}
		return results;
	}
}

/**
 * Parse a single SSE line into a typed event.
 *
 * Maintained for backwards compatibility in unit tests and callers.
 */
export function parseSSELine(line: string): AgentStreamEvent | null {
	// Treat a single line as a one-frame event.
	// Full stream parsing happens in streamFromResponse().
	if (!line.trim()) {
		return null;
	}
	if (line.trim().startsWith(":")) {
		return null;
	}
	const frame = line.endsWith("\n\n") ? line.slice(0, -2) : line;
	const events = parseSSEFrame(frame);
	return events[0] ?? null;
}

/**
 * Process all complete SSE frames from the current stream buffer.
 */
function drainFrames(
	buffer: string,
	onEvent: (event: AgentStreamEvent) => void
): { remaining: string } {
	let remaining = buffer;
	while (true) {
		const sepIndex = remaining.indexOf("\n\n");
		if (sepIndex < 0) {
			break;
		}
		const frame = remaining.slice(0, sepIndex);
		remaining = remaining.slice(sepIndex + 2);
		for (const event of parseSSEFrame(frame)) {
			onEvent(event);
		}
	}
	return { remaining };
}

/**
 * Normalize newlines so parsing works for both LF and CRLF streams.
 */
function normalizeNewlines(input: string): string {
	return input.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
}

/**
 * Stream events from a fetch Response body.
 *
 * Reads the response as a ReadableStream, splits by SSE frame boundaries, and parses each event.
 *
 * @param response - Fetch Response with text/event-stream body
 * @param onEvent - Called for each parsed event
 * @param onError - Called on stream error
 * @param onComplete - Called when stream finishes
 */
export async function streamFromResponse(
	response: Response,
	onEvent: (event: AgentStreamEvent) => void,
	onError: (error: Error) => void,
	onComplete: () => void
): Promise<void> {
	if (!response.ok) {
		onError(new Error(`HTTP ${response.status}: ${response.statusText}`));
		return;
	}

	if (!response.body) {
		onError(new Error("Response body is null"));
		return;
	}

	const reader = response.body.getReader();
	const decoder = new TextDecoder();
	let buffer = "";

	try {
		while (true) {
			const { done, value } = await reader.read();

			if (done) {
				// Process any remaining content in buffer (best effort)
				if (buffer.trim()) {
					for (const event of parseSSEFrame(normalizeNewlines(buffer))) {
						onEvent(event);
					}
				}
				onComplete();
				break;
			}

			// Decode chunk and add to buffer
			buffer += decoder.decode(value, { stream: true });
			buffer = normalizeNewlines(buffer);
			const drained = drainFrames(buffer, onEvent);
			buffer = drained.remaining;
		}
	} catch (error) {
		onError(error instanceof Error ? error : new Error("Stream reading failed"));
	} finally {
		reader.releaseLock();
	}
}
